import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { DownloadIcon, SearchIcon } from '@heroicons/react/outline'

function ResponsesTable({ responses, disclosures }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedDisclosure, setSelectedDisclosure] = useState('all')
  const [agreedFilter, setAgreedFilter] = useState('all')

  const filteredResponses = useMemo(() => {
    return responses.filter((response) => {
      const matchesSearch =
        !searchTerm ||
        response.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        response.disclosureName?.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesDisclosure = selectedDisclosure === 'all' || response.disclosureId?.toString() === selectedDisclosure

      const matchesAgreed =
        agreedFilter === 'all' ||
        (agreedFilter === 'agreed' && response.agreed) ||
        (agreedFilter === 'declined' && !response.agreed)

      return matchesSearch && matchesDisclosure && matchesAgreed
    })
  }, [responses, searchTerm, selectedDisclosure, agreedFilter])

  const exportToCSV = () => {
    const headers = ['Student Name', 'Disclosure', 'Status', 'Date', 'Respondent']
    const csvData = [
      headers.join(','),
      ...filteredResponses.map((response) =>
        [
          `"${response.studentName || 'N/A'}"`,
          `"${response.disclosureName || 'N/A'}"`,
          response.agreed ? 'Agreed' : 'Declined',
          response.date ? new Date(response.date).toLocaleDateString() : 'N/A',
          `"${response.respondentName || 'N/A'}"`
        ].join(',')
      )
    ].join('\n')

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `disclosure-responses-${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <h3 className="text-lg font-medium text-gray-900">Disclosure Responses</h3>
        <button
          onClick={exportToCSV}
          disabled={filteredResponses.length === 0}
          className="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md sm:w-auto hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <DownloadIcon className="w-4 h-4 mr-2" />
          Export to CSV
        </button>
      </div>

      <div className="flex flex-col gap-4 md:flex-row">
        <div className="relative md:flex-1">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <SearchIcon className="w-5 h-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by student name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full py-2 pl-10 pr-3 leading-5 text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
          />
        </div>

        <select
          value={selectedDisclosure}
          onChange={(e) => setSelectedDisclosure(e.target.value)}
          className="block w-full py-2 pl-3 pr-10 text-base border-gray-300 rounded-md md:flex-1 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
        >
          <option value="all">All Disclosures</option>
          {disclosures.map((disclosure) => (
            <option key={disclosure.id} value={disclosure.id}>
              {disclosure.name}
            </option>
          ))}
        </select>

        <select
          value={agreedFilter}
          onChange={(e) => setAgreedFilter(e.target.value)}
          className="block w-full py-2 pl-3 pr-10 text-base border-gray-300 rounded-md md:flex-1 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
        >
          <option value="all">All Statuses</option>
          <option value="agreed">Agreed</option>
          <option value="declined">Declined</option>
        </select>
      </div>

      <div className="hidden overflow-hidden border border-gray-200 rounded-lg lg:block">
        <div className="overflow-x-auto" style={{ maxHeight: '400px' }}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                >
                  Student Name
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                >
                  Disclosure
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
                >
                  Date
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                >
                  Respondent
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredResponses.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    {responses.length === 0 ? 'No responses yet.' : 'No responses match your filters.'}
                  </td>
                </tr>
              ) : (
                filteredResponses.map((response) => (
                  <tr key={response.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{response.studentName || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{response.disclosureName || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      {response.agreed ? (
                        <span className="inline-flex px-2 text-xs font-semibold leading-5 text-green-800 bg-green-100 rounded-full">
                          Agreed
                        </span>
                      ) : (
                        <span className="inline-flex px-2 text-xs font-semibold leading-5 text-red-800 bg-red-100 rounded-full">
                          Declined
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      <div className="text-sm text-gray-500">{formatDate(response.date)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{response.respondentName || 'N/A'}</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-3 lg:hidden">
        {filteredResponses.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500 bg-white border border-gray-200 rounded-lg">
            {responses.length === 0 ? 'No responses yet.' : 'No responses match your filters.'}
          </div>
        ) : (
          filteredResponses.map((response) => (
            <div key={response.id} className="p-4 bg-white border border-gray-200 rounded-lg">
              <div className="mb-2">
                <h4 className="text-base font-medium text-gray-900">{response.studentName || 'N/A'}</h4>
                <p className="text-sm text-gray-600">{response.disclosureName || 'N/A'}</p>
              </div>

              <div className="flex items-center gap-2 mb-2">
                {response.agreed ? (
                  <span className="inline-flex px-2 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full">
                    Agreed
                  </span>
                ) : (
                  <span className="inline-flex px-2 py-1 text-xs font-semibold text-red-800 bg-red-100 rounded-full">
                    Declined
                  </span>
                )}
                <span className="text-sm text-gray-500">• {formatDate(response.date)}</span>
              </div>

              {response.respondentName && (
                <p className="text-sm text-gray-600">Responded by: {response.respondentName}</p>
              )}
            </div>
          ))
        )}
      </div>

      <div className="text-sm text-gray-500">
        Showing {filteredResponses.length} of {responses.length} responses
      </div>
    </div>
  )
}

ResponsesTable.propTypes = {
  responses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      studentName: PropTypes.string,
      disclosureName: PropTypes.string,
      disclosureId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      agreed: PropTypes.bool.isRequired,
      date: PropTypes.string,
      respondentName: PropTypes.string
    })
  ).isRequired,
  disclosures: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired
}

export default ResponsesTable
